/**
 * Braynewave's own books in its console (R189; docs/specs/cloud/platform-costs.md): the costs it received (supplier
 * invoices), their checks and AUD amounts, the Australian financial year and its quarters, and the profit and loss
 * of the invoices Braynewave issued against those costs, with the GST collected and paid for the BAS. Amounts are
 * summed in whole cents.
 */
import { abnDigits, abnValid, formatAbn, type SupplierInvoiceValues } from './supplier-invoice-reading.js';

/** Braynewave's own ABN (decision 833): printed on invoices as the customer's, never a supplier's. */
export const BRAYNEWAVE_ABN = '82895579345';

// ------------------------------------------------------------------------------------------------ categories

/** The seeded categories, with the code the reader suggests from a supplier's name (spec §3). */
export const SEEDED_COST_CATEGORIES: ReadonlyArray<{ readonly code: string; readonly label: string }> = [
  { code: 'hosting', label: 'Hosting (Supabase)' },
  { code: 'cloudflare', label: 'Cloudflare' },
  { code: 'powersync', label: 'PowerSync' },
  { code: 'resend', label: 'Resend' },
  { code: 'apple', label: 'Apple' },
  { code: 'google', label: 'Google and Firebase' },
  { code: 'expo', label: 'Expo' },
  { code: 'github', label: 'GitHub' },
  { code: 'anthropic', label: 'Anthropic' },
  { code: 'domains', label: 'Domains' },
  { code: 'other', label: 'Other' },
];

export interface CostCategory {
  readonly id: string;
  readonly code: string | null;
  readonly label: string;
  readonly sort: number;
  readonly retired: boolean;
}

/** Why a category name cannot be saved, or null. */
export function categoryLabelProblem(
  label: string,
  others: readonly CostCategory[],
  id: string | null,
): string | null {
  const l = label.trim();
  if (l.length < 1 || l.length > 60) return 'A category name is 1 to 60 characters.';
  if (others.some((c) => c.id !== id && c.label.trim().toLowerCase() === l.toLowerCase()))
    return 'There is already a category with that name.';
  return null;
}

// ------------------------------------------------------------------------------------------------ money

/** Whole cents of an amount. */
export const cents = (n: number): number => Math.round(n * 100);
const dollars = (c: number): number => c / 100;

/** A cost as the profit and loss reads it. */
export interface CostAmounts {
  readonly currency: string;
  /** In the invoice's currency. */
  readonly amountExGst: number;
  readonly gst: number;
  readonly total: number;
  /** What the bank charged in AUD; required when the currency is not AUD. */
  readonly audTotal: number | null;
}

/**
 * A cost in AUD: as it is in AUD; otherwise the AUD total split as the invoice splits its own total (GST in
 * proportion, to the cent, the rest ex GST). A foreign invoice without its AUD total counts as nothing.
 */
export function costAud(c: CostAmounts): {
  readonly exGst: number;
  readonly gst: number;
  readonly total: number;
} {
  if (c.currency === 'AUD') return { exGst: c.amountExGst, gst: c.gst, total: c.total };
  if (c.audTotal === null) return { exGst: 0, gst: 0, total: 0 };
  const total = cents(c.audTotal);
  const gst = cents(c.total) === 0 ? 0 : Math.round((total * cents(c.gst)) / cents(c.total));
  return { exGst: dollars(total - gst), gst: dollars(gst), total: dollars(total) };
}

/** The cost form's values as typed. */
export interface CostDraft {
  readonly supplier: string;
  readonly supplierAbn: string;
  readonly invoiceNumber: string;
  readonly invoiceDate: string;
  readonly dueDate: string;
  readonly description: string;
  readonly categoryId: string;
  readonly currency: string;
  readonly amountExGst: number | null;
  readonly gst: number | null;
  readonly total: number | null;
  readonly audTotal: number | null;
  readonly paidOn: string;
  readonly notes: string;
}

export const EMPTY_COST_DRAFT: CostDraft = {
  supplier: '',
  supplierAbn: '',
  invoiceNumber: '',
  invoiceDate: '',
  dueDate: '',
  description: '',
  categoryId: '',
  currency: 'AUD',
  amountExGst: null,
  gst: null,
  total: null,
  audTotal: null,
  paidOn: '',
  notes: '',
};

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** What is wrong with a cost before it is saved (empty when it can be). */
export function costIssues(d: CostDraft): string[] {
  const out: string[] = [];
  const supplier = d.supplier.trim();
  if (supplier.length < 1 || supplier.length > 120) out.push('Enter the supplier (up to 120 characters).');
  if (d.supplierAbn.trim() !== '' && !(abnDigits(d.supplierAbn) && abnValid(d.supplierAbn)))
    out.push('The ABN is 11 digits and its check digit does not match: check it against the invoice.');
  if (d.invoiceNumber.trim().length > 60) out.push('The invoice number is at most 60 characters.');
  if (!ISO.test(d.invoiceDate)) out.push('Enter the invoice date.');
  if (d.dueDate && !ISO.test(d.dueDate)) out.push('The due date is not a date.');
  if (d.dueDate && ISO.test(d.invoiceDate) && d.dueDate < d.invoiceDate)
    out.push('The due date is before the invoice date.');
  if (d.description.trim().length > 500) out.push('The description is at most 500 characters.');
  if (!d.categoryId) out.push('Choose a category.');
  if (!/^[A-Z]{3}$/.test(d.currency)) out.push('The currency is three capital letters, for example AUD.');
  const money = (n: number | null): boolean => n !== null && Number.isFinite(n) && Math.abs(n) <= 10_000_000;
  if (!money(d.amountExGst)) out.push('Enter the amount ex GST.');
  if (!money(d.gst)) out.push('Enter the GST (0 when the invoice has none).');
  if (money(d.amountExGst) && money(d.gst)) {
    const ex = cents(d.amountExGst!);
    const g = cents(d.gst!);
    if ((ex >= 0 && g < 0) || (ex < 0 && g > 0))
      out.push('The GST has the sign of the amount (a credit note’s is 0 or less).');
    else if (Math.abs(g) > Math.abs(ex)) out.push('The GST is more than the amount ex GST: check both.');
    if (money(d.total) && cents(d.total!) !== ex + g)
      out.push('The total is not the amount ex GST plus the GST: check the three against the invoice.');
  }
  if (!money(d.total)) out.push('Enter the total.');
  if (d.currency !== 'AUD' && /^[A-Z]{3}$/.test(d.currency) && !money(d.audTotal))
    out.push(`Enter what the bank charged in AUD for this ${d.currency} invoice.`);
  if (d.paidOn && !ISO.test(d.paidOn)) out.push('The paid date is not a date.');
  if (d.notes.length > 1000) out.push('Notes are at most 1000 characters.');
  return out;
}

/** The total of an amount ex GST and its GST, to the cent (null until both are known). */
export function costTotalOf(exGst: number | null, gst: number | null): number | null {
  return exGst === null || gst === null ? null : dollars(cents(exGst) + cents(gst));
}

// ------------------------------------------------------------------------------------------------ periods

export type PeriodGrain = 'month' | 'quarter' | 'fy';

/** The financial year a date falls in, by the year it ends: 1 Jul 2026 to 30 Jun 2027 is 2027. */
export function financialYearOf(iso: string): number {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  return m >= 7 ? y + 1 : y;
}

/** `FY 2026–27` for the year ending in 2027. */
export function financialYearLabel(fy: number): string {
  return `FY ${fy - 1}–${String(fy).slice(2)}`;
}

/** The first and last day of a financial year. */
export function financialYearRange(fy: number): { readonly from: string; readonly to: string } {
  return { from: `${fy - 1}-07-01`, to: `${fy}-06-30` };
}

const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const pad = (n: number): string => String(n).padStart(2, '0');
const lastDay = (y: number, m: number): number => new Date(Date.UTC(y, m, 0)).getUTCDate();

/** One period of the profit and loss. `key` sorts in time order. */
export interface Period {
  readonly key: string;
  readonly label: string;
  readonly from: string;
  readonly to: string;
}

/** The period of a grain a date falls in. Quarters are the financial year's (Q1 is Jul–Sep, as the BAS). */
export function periodOf(iso: string, grain: PeriodGrain): Period {
  const y = Number(iso.slice(0, 4));
  const m = Number(iso.slice(5, 7));
  if (grain === 'month')
    return {
      key: `${y}-${pad(m)}`,
      label: `${MONTH_SHORT[m - 1]} ${y}`,
      from: `${y}-${pad(m)}-01`,
      to: `${y}-${pad(m)}-${pad(lastDay(y, m))}`,
    };
  const fy = financialYearOf(iso);
  if (grain === 'fy') return { key: `FY${fy}`, ...financialYearRange(fy), label: financialYearLabel(fy) };
  // quarter of the financial year: Jul–Sep 1, Oct–Dec 2, Jan–Mar 3, Apr–Jun 4
  const q = m >= 7 ? Math.floor((m - 7) / 3) + 1 : Math.floor((m - 1) / 3) + 3;
  const first = m - ((m - 1) % 3);
  const last = first + 2;
  return {
    key: `FY${fy}-Q${q}`,
    label: `${MONTH_SHORT[first - 1]}–${MONTH_SHORT[last - 1]} ${y} (Q${q} ${financialYearLabel(fy)})`,
    from: `${y}-${pad(first)}-01`,
    to: `${y}-${pad(last)}-${pad(lastDay(y, last))}`,
  };
}

/** Every period of a grain from the one holding `from` to the one holding `to`, in order. */
export function periodsBetween(from: string, to: string, grain: PeriodGrain): Period[] {
  const out: Period[] = [];
  if (to < from) return out;
  let p = periodOf(from, grain);
  for (let i = 0; i < 1200; i += 1) {
    out.push(p);
    if (p.to >= to) break;
    const next = new Date(`${p.to}T00:00:00Z`);
    next.setUTCDate(next.getUTCDate() + 1);
    p = periodOf(next.toISOString().slice(0, 10), grain);
  }
  return out;
}

/** The costs list's period choices (spec §2). */
export type CostPeriodChoice = 'this_fy' | 'last_fy' | 'this_month' | 'last_month' | 'this_quarter' | 'all';

/** The dates a period choice covers on a day (`today` as an ISO date); null for every cost. */
export function costPeriodRange(
  choice: CostPeriodChoice,
  today: string,
): { readonly from: string; readonly to: string } | null {
  const fy = financialYearOf(today);
  if (choice === 'all') return null;
  if (choice === 'this_fy') return financialYearRange(fy);
  if (choice === 'last_fy') return financialYearRange(fy - 1);
  if (choice === 'this_quarter') return periodOf(today, 'quarter');
  const month = periodOf(today, 'month');
  if (choice === 'this_month') return month;
  const before = new Date(`${month.from}T00:00:00Z`);
  before.setUTCDate(0);
  return periodOf(before.toISOString().slice(0, 10), 'month');
}

// ------------------------------------------------------------------------------------------------ profit and loss

/** An invoice Braynewave issued (invoice_record), as the profit and loss reads it. */
export interface IssuedInvoice {
  readonly issuedOn: string;
  /** The total invoiced, including any GST. */
  readonly amount: number;
  /** The GST included in the amount. */
  readonly gst: number;
  readonly currency: string;
  readonly status: 'issued' | 'paid' | 'void';
}

/** A cost Braynewave received, as the profit and loss reads it. */
export interface ReceivedCost extends CostAmounts {
  readonly invoiceDate: string;
  readonly deleted: boolean;
}

export interface ProfitLossRow {
  readonly period: Period;
  readonly incomeExGst: number;
  readonly incomePaidExGst: number;
  readonly incomeUnpaidExGst: number;
  readonly costsExGst: number;
  readonly profit: number;
  readonly gstCollected: number;
  readonly gstPaid: number;
  /** Collected − paid: positive is owed to the ATO. */
  readonly gstNet: number;
  readonly invoices: number;
  readonly costs: number;
}

export interface ProfitLoss {
  readonly rows: readonly ProfitLossRow[];
  readonly total: Omit<ProfitLossRow, 'period'>;
  /** Invoices issued in another currency, left out (spec §6). */
  readonly leftOut: ReadonlyArray<{ readonly currency: string; readonly count: number }>;
  readonly gstRegistered: boolean;
}

export interface ProfitLossInput {
  readonly invoices: readonly IssuedInvoice[];
  readonly costs: readonly ReceivedCost[];
  readonly grain: PeriodGrain;
  readonly from: string;
  readonly to: string;
  /** False: GST on costs is part of the cost and no GST is shown (a sole trader not registered for GST). */
  readonly gstRegistered?: boolean;
}

interface Sums {
  income: number;
  paid: number;
  costs: number;
  gstIn: number;
  gstOut: number;
  invoices: number;
  costsN: number;
}

const zero = (): Sums => ({ income: 0, paid: 0, costs: 0, gstIn: 0, gstOut: 0, invoices: 0, costsN: 0 });

function rowOf(s: Sums, gstRegistered: boolean): Omit<ProfitLossRow, 'period'> {
  const gstIn = gstRegistered ? s.gstIn : 0;
  const gstOut = gstRegistered ? s.gstOut : 0;
  return {
    incomeExGst: dollars(s.income),
    incomePaidExGst: dollars(s.paid),
    incomeUnpaidExGst: dollars(s.income - s.paid),
    costsExGst: dollars(s.costs),
    profit: dollars(s.income - s.costs),
    gstCollected: dollars(gstIn),
    gstPaid: dollars(gstOut),
    gstNet: dollars(gstIn - gstOut),
    invoices: s.invoices,
    costs: s.costsN,
  };
}

/**
 * The profit and loss per period (spec §6): income is the invoices issued (not void, AUD) ex GST by issue date,
 * costs the costs received (not deleted) ex GST in AUD by invoice date, profit the difference; GST collected and
 * paid beside. Every period of the range is a row, empty ones too.
 */
export function profitAndLoss(input: ProfitLossInput): ProfitLoss {
  const gstRegistered = input.gstRegistered ?? true;
  const periods = periodsBetween(input.from, input.to, input.grain);
  const sums = new Map(periods.map((p) => [p.key, zero()]));
  const all = zero();
  const leftOut = new Map<string, number>();
  const inRange = (d: string): boolean => d >= input.from && d <= input.to;

  for (const i of input.invoices) {
    if (i.status === 'void' || !inRange(i.issuedOn)) continue;
    if (i.currency !== 'AUD') {
      leftOut.set(i.currency, (leftOut.get(i.currency) ?? 0) + 1);
      continue;
    }
    const s = sums.get(periodOf(i.issuedOn, input.grain).key);
    if (!s) continue;
    const ex = cents(i.amount) - cents(i.gst);
    for (const t of [s, all]) {
      t.income += ex;
      if (i.status === 'paid') t.paid += ex;
      t.gstIn += cents(i.gst);
      t.invoices += 1;
    }
  }
  for (const c of input.costs) {
    if (c.deleted || !inRange(c.invoiceDate)) continue;
    const s = sums.get(periodOf(c.invoiceDate, input.grain).key);
    if (!s) continue;
    const aud = costAud(c);
    const cost = gstRegistered ? cents(aud.exGst) : cents(aud.total);
    for (const t of [s, all]) {
      t.costs += cost;
      t.gstOut += cents(aud.gst);
      t.costsN += 1;
    }
  }
  return {
    rows: periods.map((p) => ({ period: p, ...rowOf(sums.get(p.key)!, gstRegistered) })),
    total: rowOf(all, gstRegistered),
    leftOut: [...leftOut.entries()].sort().map(([currency, count]) => ({ currency, count })),
    gstRegistered,
  };
}

/** One CSV cell: quoted when needed; text that starts like a formula is written as text. */
function csvText(s: string): string {
  let v = s;
  if (/^[=+\-@]/.test(v)) v = `'${v}`;
  return /[",\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}
const csvMoney = (n: number): string => n.toFixed(2);

/** The profit and loss as CSV, one row per period and a total row (spec §6). */
export function profitAndLossCsv(p: ProfitLoss): string {
  const head = ['Period', 'From', 'To', 'Income ex GST (AUD)', 'Income paid', 'Income not paid yet'];
  head.push(p.gstRegistered ? 'Costs ex GST (AUD)' : 'Costs incl GST (AUD)', 'Profit (AUD)');
  if (p.gstRegistered) head.push('GST collected', 'GST paid', 'GST net');
  head.push('Invoices', 'Costs');
  const line = (label: string, from: string, to: string, r: Omit<ProfitLossRow, 'period'>): string => {
    const cells = [csvText(label), from, to];
    cells.push(
      ...[r.incomeExGst, r.incomePaidExGst, r.incomeUnpaidExGst, r.costsExGst, r.profit].map(csvMoney),
    );
    if (p.gstRegistered) cells.push(...[r.gstCollected, r.gstPaid, r.gstNet].map(csvMoney));
    cells.push(String(r.invoices), String(r.costs));
    return cells.join(',');
  };
  const first = p.rows[0]?.period.from ?? '';
  const last = p.rows[p.rows.length - 1]?.period.to ?? '';
  return (
    [
      head.map(csvText).join(','),
      ...p.rows.map((r) => line(r.period.label, r.period.from, r.period.to, r)),
      line('Total', first, last, p.total),
    ].join('\r\n') + '\r\n'
  );
}

/** Totals of a list of costs in AUD (the Costs tab's line under its filters). */
export function costTotals(costs: readonly CostAmounts[]): {
  readonly exGst: number;
  readonly gst: number;
  readonly total: number;
  readonly missingAud: number;
} {
  let ex = 0;
  let gst = 0;
  let total = 0;
  let missingAud = 0;
  for (const c of costs) {
    if (c.currency !== 'AUD' && c.audTotal === null) missingAud += 1;
    const a = costAud(c);
    ex += cents(a.exGst);
    gst += cents(a.gst);
    total += cents(a.total);
  }
  return { exGst: dollars(ex), gst: dollars(gst), total: dollars(total), missingAud };
}

/** A reading (Claude's or the text layer's) as the cost form's values; the admin checks them before saving. */
export function costDraftFromReading(v: SupplierInvoiceValues, categoryId: string): CostDraft {
  return {
    ...EMPTY_COST_DRAFT,
    supplier: v.supplier ?? '',
    supplierAbn: v.supplier_abn ? formatAbn(v.supplier_abn) : '',
    invoiceNumber: v.invoice_number ?? '',
    invoiceDate: v.invoice_date ?? '',
    dueDate: v.due_date ?? '',
    description: v.description ?? '',
    categoryId,
    currency: v.currency ?? 'AUD',
    amountExGst: v.amount_ex_gst,
    gst: v.gst,
    total: v.total ?? costTotalOf(v.amount_ex_gst, v.gst),
    paidOn: v.paid_on ?? '',
  };
}
